// Tutorial data - Enhanced with Zero-Knowledge and Privacy concepts
export const tutorials = [
  // Visual/Non-code tutorials for beginners
  {
    id: 'privacy-basics',
    title: 'Privacy Basics Explained',
    category: 'tutorials',
    difficulty: 'Beginner',
    description: 'Learn the fundamentals of privacy and zero-knowledge in simple terms',
    visualTutorial: true,
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
        <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2"/>
      </svg>
    ),
    steps: [
      {
        id: 'step-1',
        title: 'What is Privacy?',
        content: `Privacy means controlling what information you share and with whom.

**In everyday life:**
- You don't show your bank balance to strangers
- You don't tell everyone your home address
- You keep some conversations private

**In digital life:**
- Your transactions shouldn't be visible to everyone
- Your identity should stay private when possible
- You should choose what to reveal

**Why it matters:** Privacy gives you control. Without it, others can track you, target you, or even steal from you.`,
        visual: (
          <svg width="200" height="150" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        )
      },
      {
        id: 'step-2',
        title: 'The Problem with Public Blockchains',
        content: `Most blockchains are completely transparent - everyone can see everything.

**What's visible:**
- Every transaction amount
- Who sent money to whom
- Account balances
- Transaction history

**The risks:**
- People can track your spending
- Your wealth is visible to everyone
- Your identity can be linked to addresses
- Targeted attacks become possible

**Example:** If you pay for coffee, everyone knows:
- How much you paid
- Where you were
- What time it was
- Your account balance

This is why privacy matters!`,
        visual: (
          <svg width="200" height="150" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="2" x2="12" y2="6"/>
            <line x1="12" y1="18" x2="12" y2="22"/>
            <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/>
            <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/>
            <line x1="2" y1="12" x2="6" y2="12"/>
            <line x1="18" y1="12" x2="22" y2="12"/>
            <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/>
            <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/>
          </svg>
        )
      },
      {
        id: 'step-3',
        title: 'How Zero-Knowledge Solves This',
        content: `Zero-knowledge proofs let you prove something is true without revealing the details.

**The magic:**
- Prove you paid without showing the amount
- Prove you're old enough without showing your age
- Prove you have funds without revealing your balance

**How it works:**
1. You do a computation on your device
2. Your device creates a proof
3. The proof shows the result is correct
4. But it doesn't reveal the inputs

**Real example:**
Instead of showing "I paid $50", you prove "I paid between $40-$60" or just "I paid correctly" - without revealing the exact amount!

This is the power of zero-knowledge technology.`,
        visual: (
          <svg width="200" height="150" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            <path d="M9 12l2 2 4-4" strokeWidth="3"/>
          </svg>
        )
      }
    ]
  },
  {
    id: 'how-miden-works',
    title: 'How Miden Works',
    category: 'tutorials',
    difficulty: 'Beginner',
    description: 'Understand the flow of transactions in Miden from start to finish',
    visualTutorial: true,
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M13 7l5 5-5 5M6 7l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    steps: [
      {
        id: 'step-1',
        title: 'Step 1: You Initiate a Transaction',
        content: `Everything starts on your device - your computer, phone, or wallet.

**What happens:**
- You decide to send money or perform an action
- Your wallet prepares the transaction
- All the details stay on your device

**Privacy benefit:** Nothing leaves your device yet. Your transaction is completely private at this stage.`,
        visual: (
          <svg width="200" height="150" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="2" y="3" width="20" height="14" rx="2"/>
            <line x1="8" y1="21" x2="16" y2="21"/>
          </svg>
        )
      },
      {
        id: 'step-2',
        title: 'Step 2: Miden VM Runs Locally',
        content: `The Miden Virtual Machine runs on YOUR device, not on the blockchain.

**What happens:**
- Your device executes the transaction logic
- It processes all the calculations
- It verifies everything is correct

**Why this matters:** 
- Faster: No waiting for blockchain confirmation
- Cheaper: Less computation on-chain
- Private: Everything happens on your device

**The result:** Your device knows the transaction is valid, but hasn't shared any details yet.`,
        visual: (
          <svg width="200" height="150" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="3"/>
            <path d="M12 1v6m0 6v6M5.64 5.64l4.24 4.24m4.24 4.24l4.24 4.24M1 12h6m6 0h6M5.64 18.36l4.24-4.24m4.24-4.24l4.24-4.24"/>
          </svg>
        )
      },
      {
        id: 'step-3',
        title: 'Step 3: A Proof is Created',
        content: `Your device creates a mathematical proof that the transaction is valid.

**What the proof contains:**
- Proof that the math is correct
- Proof that you have sufficient funds
- Proof that the transaction is valid

**What it doesn't contain:**
- The exact amounts
- Your account balance
- Transaction details

**The proof is tiny:** Just a few kilobytes, even for complex transactions!`,
        visual: (
          <svg width="200" height="150" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            <path d="M9 12l2 2 4-4" strokeWidth="3"/>
          </svg>
        )
      },
      {
        id: 'step-4',
        title: 'Step 4: Proof Goes to Blockchain',
        content: `Only the proof is sent to the blockchain - not your transaction data.

**What happens:**
- The tiny proof is sent to Polygon (Miden's blockchain)
- The blockchain verifies the proof is valid
- The transaction is confirmed

**What stays private:**
- Your transaction amounts
- Your account details
- All the computation steps

**The result:** The blockchain knows your transaction is valid, but learns nothing about the details. This is the magic of zero-knowledge!`,
        visual: (
          <svg width="200" height="150" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="5" cy="12" r="3"/>
            <circle cx="19" cy="12" r="3"/>
            <line x1="8" y1="12" x2="16" y2="12"/>
            <path d="M9 12l2 2 4-4" strokeWidth="3"/>
          </svg>
        )
      }
    ]
  },
  // Beginner tutorials
  {
    id: 'merkle-tree',
    title: "What's a Merkle Tree?",
    category: 'tutorials',
    difficulty: 'Beginner',
    description: 'Interactive guide to understanding Merkle trees',
    visualTutorial: true,
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M12 2v6M12 8l-4-4M12 8l4-4M8 8v6M16 8v6M8 14l-4 4M16 14l4 4M8 18v4M16 18v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    steps: [
      {
        id: 'step-1',
        title: 'What is a Merkle Tree?',
        content: `A Merkle tree is a data structure that allows efficient and secure verification of large datasets. Think of it like a family tree, but for data!

**Key Concept:** Instead of checking every piece of data individually, you can verify the entire dataset by checking just one "root hash" at the top.

**Why it matters:** In blockchain and zero-knowledge systems, Merkle trees let you prove you have specific data without revealing everything else. It's like proving you have a specific book in a library without showing all the books!

**Simple Example:**
- You have 4 pieces of data: A, B, C, D
- Each gets hashed: H(A), H(B), H(C), H(D)
- Pairs are combined and hashed: H(H(A)+H(B)), H(H(C)+H(D))
- Finally, the root: H(H(H(A)+H(B)) + H(H(C)+H(D)))

This root hash represents the entire dataset!`,
        visual: (
          <svg width="300" height="200" viewBox="0 0 300 200" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="150" cy="30" r="20" fill="rgba(249, 115, 22, 0.2)"/>
            <text x="150" y="35" textAnchor="middle" fill="currentColor" fontSize="12" >Root</text>
            <line x1="150" y1="50" x2="100" y2="80"/>
            <line x1="150" y1="50" x2="200" y2="80"/>
            <circle cx="100" cy="100" r="18" fill="rgba(249, 115, 22, 0.15)"/>
            <text x="100" y="105" textAnchor="middle" fill="currentColor" fontSize="10">P1</text>
            <circle cx="200" cy="100" r="18" fill="rgba(249, 115, 22, 0.15)"/>
            <text x="200" y="105" textAnchor="middle" fill="currentColor" fontSize="10">P2</text>
            <line x1="100" y1="118" x2="60" y2="150"/>
            <line x1="100" y1="118" x2="140" y2="150"/>
            <line x1="200" y1="118" x2="160" y2="150"/>
            <line x1="200" y1="118" x2="240" y2="150"/>
            <circle cx="60" cy="170" r="15" fill="rgba(16, 185, 129, 0.2)"/>
            <text x="60" y="175" textAnchor="middle" fill="currentColor" fontSize="9">A</text>
            <circle cx="140" cy="170" r="15" fill="rgba(16, 185, 129, 0.2)"/>
            <text x="140" y="175" textAnchor="middle" fill="currentColor" fontSize="9">B</text>
            <circle cx="160" cy="170" r="15" fill="rgba(16, 185, 129, 0.2)"/>
            <text x="160" y="175" textAnchor="middle" fill="currentColor" fontSize="9">C</text>
            <circle cx="240" cy="170" r="15" fill="rgba(16, 185, 129, 0.2)"/>
            <text x="240" y="175" textAnchor="middle" fill="currentColor" fontSize="9">D</text>
          </svg>
        )
      },
      {
        id: 'step-2',
        title: 'The Building Blocks: Hash Functions',
        content: `Before understanding Merkle trees, you need to understand hash functions.

**Hash Function:** A mathematical function that takes any input and produces a fixed-size output (like a fingerprint).

**Key Properties:**
1. **Deterministic:** Same input always produces same output
2. **One-way:** Can't reverse a hash to get the original data
3. **Avalanche effect:** Tiny change in input = completely different hash

**Example:**
- Hash("Hello") = 8b1a9953c4611296a827abf8c47804d7
- Hash("hello") = 5d41402abc4b2a76b9719d911017c592 (completely different!)

**Why hashes matter:** In Merkle trees, each piece of data is hashed, and these hashes are combined to create the tree structure.`,
        visual: (
          <svg width="300" height="150" viewBox="0 0 300 150" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="20" y="20" width="80" height="40" rx="4" fill="rgba(100, 116, 139, 0.1)"/>
            <text x="60" y="45" textAnchor="middle" fill="currentColor" fontSize="12">"Hello"</text>
            <path d="M100 40 L140 40" strokeDasharray="5,5"/>
            <text x="120" y="35" textAnchor="middle" fill="currentColor" fontSize="10">Hash</text>
            <rect x="140" y="20" width="80" height="40" rx="4" fill="rgba(249, 115, 22, 0.1)"/>
            <text x="180" y="40" textAnchor="middle" fill="currentColor" fontSize="9">8b1a9953...</text>
            <text x="180" y="55" textAnchor="middle" fill="currentColor" fontSize="9">(fingerprint)</text>
            <rect x="20" y="90" width="80" height="40" rx="4" fill="rgba(100, 116, 139, 0.1)"/>
            <text x="60" y="115" textAnchor="middle" fill="currentColor" fontSize="12">"hello"</text>
            <path d="M100 110 L140 110" strokeDasharray="5,5"/>
            <rect x="140" y="90" width="80" height="40" rx="4" fill="rgba(249, 115, 22, 0.1)"/>
            <text x="180" y="110" textAnchor="middle" fill="currentColor" fontSize="9">5d41402a...</text>
            <text x="180" y="125" textAnchor="middle" fill="currentColor" fontSize="9">(different!)</text>
          </svg>
        )
      },
      {
        id: 'step-3',
        title: 'Building a Simple Merkle Tree',
        content: `Let's build a Merkle tree step by step with 4 data items.

**Step 1:** Start with your data
- Data: ["Alice", "Bob", "Charlie", "Diana"]

**Step 2:** Hash each item (leaf nodes)
- H("Alice") = hash_A
- H("Bob") = hash_B
- H("Charlie") = hash_C
- H("Diana") = hash_D

**Step 3:** Combine pairs (parent nodes)
- Parent 1: H(hash_A + hash_B)
- Parent 2: H(hash_C + hash_D)

**Step 4:** Create the root
- Root: H(Parent 1 + Parent 2)

**Visual Structure:**
The tree builds from bottom to top, combining hashes at each level until reaching the root.

The root hash is your proof that all data exists!`,
        visual: (
          <svg width="300" height="250" viewBox="0 0 300 250" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="150" cy="30" r="25" fill="rgba(249, 115, 22, 0.2)"/>
            <text x="150" y="35" textAnchor="middle" fill="currentColor" fontSize="11" >Root Hash</text>
            <line x1="150" y1="55" x2="90" y2="90"/>
            <line x1="150" y1="55" x2="210" y2="90"/>
            <circle cx="90" cy="110" r="20" fill="rgba(249, 115, 22, 0.15)"/>
            <text x="90" y="115" textAnchor="middle" fill="currentColor" fontSize="10">Parent 1</text>
            <circle cx="210" cy="110" r="20" fill="rgba(249, 115, 22, 0.15)"/>
            <text x="210" y="115" textAnchor="middle" fill="currentColor" fontSize="10">Parent 2</text>
            <line x1="90" y1="130" x2="50" y2="170"/>
            <line x1="90" y1="130" x2="130" y2="170"/>
            <line x1="210" y1="130" x2="170" y2="170"/>
            <line x1="210" y1="130" x2="250" y2="170"/>
            <rect x="30" y="180" width="40" height="30" rx="4" fill="rgba(16, 185, 129, 0.2)"/>
            <text x="50" y="200" textAnchor="middle" fill="currentColor" fontSize="10">Alice</text>
            <rect x="110" y="180" width="40" height="30" rx="4" fill="rgba(16, 185, 129, 0.2)"/>
            <text x="130" y="200" textAnchor="middle" fill="currentColor" fontSize="10">Bob</text>
            <rect x="150" y="180" width="40" height="30" rx="4" fill="rgba(16, 185, 129, 0.2)"/>
            <text x="170" y="200" textAnchor="middle" fill="currentColor" fontSize="10">Charlie</text>
            <rect x="230" y="180" width="40" height="30" rx="4" fill="rgba(16, 185, 129, 0.2)"/>
            <text x="250" y="200" textAnchor="middle" fill="currentColor" fontSize="10">Diana</text>
          </svg>
        )
      },
      {
        id: 'step-4',
        title: 'Merkle Proofs: Proving Without Revealing',
        content: `Here's the magic of Merkle trees: **Merkle Proofs**!

**The Problem:** You want to prove you have "Bob" in a dataset of 1 million items, but you don't want to reveal all 1 million items.

**The Solution:** Merkle proof!

**How it works:**
1. You provide "Bob" (the data you want to prove)
2. You provide the "sibling hashes" needed to reconstruct the path to root
3. Anyone can verify by:
   - Hashing "Bob" to get hash_B
   - Combining with sibling hashes up to root
   - Checking if it matches the known root hash

**Example Proof Path:**
- You have: "Bob" + hash_A (sibling) + Parent2 (sibling)
- Verifier computes: H(H(hash_A + H("Bob")) + Parent2)
- If it matches root → proof is valid!

**Privacy Benefit:** You only reveal "Bob" and a few hashes, not the entire dataset!`,
        visual: (
          <svg width="300" height="200" viewBox="0 0 300 200" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="20" y="20" width="260" height="160" rx="8" fill="rgba(15, 23, 42, 0.6)" strokeDasharray="4,4"/>
            <text x="150" y="45" textAnchor="middle" fill="currentColor" fontSize="14" >Merkle Proof Path</text>
            <circle cx="150" cy="80" r="30" fill="rgba(16, 185, 129, 0.3)" strokeWidth="3"/>
            <text x="150" y="88" textAnchor="middle" fill="currentColor" fontSize="12" >"Bob"</text>
            <text x="150" y="120" textAnchor="middle" fill="currentColor" fontSize="11">+ hash_A (sibling)</text>
            <text x="150" y="140" textAnchor="middle" fill="currentColor" fontSize="11">+ Parent2 (sibling)</text>
            <path d="M150 150 L150 170" strokeWidth="2"/>
            <text x="150" y="185" textAnchor="middle" fill="currentColor" fontSize="11" >✓ Valid Proof!</text>
          </svg>
        )
      },
      {
        id: 'step-5',
        title: 'Why Merkle Trees in Blockchain?',
        content: `Merkle trees are fundamental to blockchain technology!

**Use Cases:**

1. **Transaction Verification**
   - Block contains thousands of transactions
   - Merkle root in block header represents all transactions
   - Light clients can verify transactions without downloading entire blockchain

2. **State Verification**
   - Blockchain state is huge (millions of accounts)
   - Merkle tree organizes all account states
   - You can prove your account balance without revealing others

3. **Efficiency**
   - Instead of storing/transmitting all data
   - Store just the root hash (32 bytes)
   - Verify specific data with small proofs

**Real Example:** Bitcoin uses Merkle trees in every block. The block header contains the Merkle root of all transactions, allowing efficient verification.`,
        visual: (
          <svg width="300" height="180" viewBox="0 0 300 180" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="20" y="20" width="260" height="140" rx="6" fill="rgba(15, 23, 42, 0.6)"/>
            <text x="150" y="45" textAnchor="middle" fill="currentColor" fontSize="13" >Blockchain Block</text>
            <rect x="40" y="60" width="100" height="30" rx="4" fill="rgba(100, 116, 139, 0.2)"/>
            <text x="90" y="80" textAnchor="middle" fill="currentColor" fontSize="10">Tx 1</text>
            <rect x="160" y="60" width="100" height="30" rx="4" fill="rgba(100, 116, 139, 0.2)"/>
            <text x="210" y="80" textAnchor="middle" fill="currentColor" fontSize="10">Tx 2</text>
            <rect x="40" y="100" width="100" height="30" rx="4" fill="rgba(100, 116, 139, 0.2)"/>
            <text x="90" y="120" textAnchor="middle" fill="currentColor" fontSize="10">Tx 3</text>
            <rect x="160" y="100" width="100" height="30" rx="4" fill="rgba(100, 116, 139, 0.2)"/>
            <text x="210" y="120" textAnchor="middle" fill="currentColor" fontSize="10">Tx 4</text>
            <circle cx="150" cy="145" r="15" fill="rgba(249, 115, 22, 0.3)"/>
            <text x="150" y="150" textAnchor="middle" fill="currentColor" fontSize="9" >Root</text>
          </svg>
        )
      },
      {
        id: 'step-6',
        title: 'Merkle Trees in Zero-Knowledge Proofs',
        content: `Merkle trees are crucial for zero-knowledge proofs and privacy!

**ZK Use Case 1: Private Balances**
- Your balance is stored in a Merkle tree
- You can prove you have enough funds without revealing your exact balance
- The proof shows: "I have ≥ X tokens" without revealing the actual amount

**ZK Use Case 2: Membership Proofs**
- Prove you're in a whitelist without revealing the entire list
- Prove you voted without revealing who else voted
- Prove you have credentials without revealing all credentials

**ZK Use Case 3: Miden Notes**
- Miden uses Merkle trees to organize notes (private assets)
- You can prove note ownership without revealing other notes
- Enables private transactions while maintaining security

**Key Insight:** Merkle trees + zero-knowledge = powerful privacy! You get both security (verifiable) and privacy (selective disclosure).`,
        visual: (
          <svg width="300" height="200" viewBox="0 0 300 200" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="20" y="20" width="120" height="160" rx="6" fill="rgba(16, 185, 129, 0.1)"/>
            <text x="80" y="45" textAnchor="middle" fill="currentColor" fontSize="12" >Private</text>
            <text x="80" y="65" textAnchor="middle" fill="currentColor" fontSize="11">Your Balance</text>
            <text x="80" y="85" textAnchor="middle" fill="currentColor" fontSize="11">Your Notes</text>
            <text x="80" y="105" textAnchor="middle" fill="currentColor" fontSize="11">Your Identity</text>
            <path d="M140 100 L160 100" strokeWidth="3" strokeDasharray="8,4"/>
            <text x="150" y="95" textAnchor="middle" fill="currentColor" fontSize="10">ZK Proof</text>
            <rect x="160" y="20" width="120" height="160" rx="6" fill="rgba(249, 115, 22, 0.1)"/>
            <text x="220" y="45" textAnchor="middle" fill="currentColor" fontSize="12" >Public</text>
            <text x="220" y="70" textAnchor="middle" fill="currentColor" fontSize="10">✓ Valid Proof</text>
            <text x="220" y="95" textAnchor="middle" fill="currentColor" fontSize="10">✓ Has Funds</text>
            <text x="220" y="120" textAnchor="middle" fill="currentColor" fontSize="10">✓ Authorized</text>
            <text x="220" y="150" textAnchor="middle" fill="currentColor" fontSize="9" fillOpacity="0.6">(No details revealed)</text>
          </svg>
        )
      },
      {
        id: 'step-7',
        title: 'Merkle Tree Properties',
        content: `Understanding key properties helps you use Merkle trees effectively.

**1. Deterministic**
- Same data always produces same root hash
- Essential for verification and consensus

**2. Tamper-Proof**
- Changing any data changes the root hash
- Easy to detect if data was modified

**3. Efficient Verification**
- Proof size: O(log n) where n = number of items
- For 1 million items, proof is only ~20 hashes!
- Much better than checking all 1 million items

**4. Incremental Updates**
- Adding/removing items requires recalculating only affected branches
- Not the entire tree!

**5. Parallelizable**
- Different branches can be computed in parallel
- Speeds up tree construction

**Practical Impact:** These properties make Merkle trees perfect for blockchain, where you need to verify large datasets efficiently and securely.`,
        visual: (
          <svg width="300" height="150" viewBox="0 0 300 150" fill="none" stroke="currentColor" strokeWidth="2">
            <text x="150" y="30" textAnchor="middle" fill="currentColor" fontSize="13" >Proof Size Comparison</text>
            <rect x="20" y="50" width="120" height="20" rx="4" fill="rgba(239, 68, 68, 0.2)"/>
            <text x="80" y="65" textAnchor="middle" fill="currentColor" fontSize="11">1M items: Check all</text>
            <text x="80" y="80" textAnchor="middle" fill="currentColor" fontSize="9">1,000,000 operations</text>
            <rect x="160" y="50" width="120" height="20" rx="4" fill="rgba(16, 185, 129, 0.2)"/>
            <text x="220" y="65" textAnchor="middle" fill="currentColor" fontSize="11">1M items: Merkle proof</text>
            <text x="220" y="80" textAnchor="middle" fill="currentColor" fontSize="9">~20 hashes only!</text>
            <text x="150" y="120" textAnchor="middle" fill="currentColor" fontSize="12" >50,000x more efficient!</text>
          </svg>
        )
      },
      {
        id: 'step-8',
        title: 'Merkle Tree Variants',
        content: `Different Merkle tree variants serve different purposes.

**1. Standard Merkle Tree (Binary)**
- Each node has 2 children
- Most common type
- Simple and efficient

**2. Merkle Patricia Tree (Trie)**
- Used in Ethereum
- Optimized for key-value storage
- Efficient for sparse data

**3. Sparse Merkle Tree**
- Every possible leaf exists (even if empty)
- Perfect for proving non-membership
- "Prove this item does NOT exist"

**4. Merkle Mountain Range**
- Used in some blockchain systems
- Optimized for append-only data
- Efficient for growing datasets

**Miden's Approach:** Miden uses optimized Merkle trees for note management, allowing efficient proofs of note existence and ownership while maintaining privacy.`,
        visual: (
          <svg width="300" height="200" viewBox="0 0 300 200" fill="none" stroke="currentColor" strokeWidth="2">
            <text x="150" y="25" textAnchor="middle" fill="currentColor" fontSize="13" >Merkle Tree Types</text>
            <rect x="20" y="40" width="80" height="60" rx="4" fill="rgba(100, 116, 139, 0.1)"/>
            <text x="60" y="60" textAnchor="middle" fill="currentColor" fontSize="11" >Binary</text>
            <text x="60" y="80" textAnchor="middle" fill="currentColor" fontSize="9">2 children</text>
            <text x="60" y="95" textAnchor="middle" fill="currentColor" fontSize="9">per node</text>
            <rect x="110" y="40" width="80" height="60" rx="4" fill="rgba(100, 116, 139, 0.1)"/>
            <text x="150" y="60" textAnchor="middle" fill="currentColor" fontSize="11" >Patricia</text>
            <text x="150" y="80" textAnchor="middle" fill="currentColor" fontSize="9">Key-value</text>
            <text x="150" y="95" textAnchor="middle" fill="currentColor" fontSize="9">optimized</text>
            <rect x="200" y="40" width="80" height="60" rx="4" fill="rgba(100, 116, 139, 0.1)"/>
            <text x="240" y="60" textAnchor="middle" fill="currentColor" fontSize="11" >Sparse</text>
            <text x="240" y="80" textAnchor="middle" fill="currentColor" fontSize="9">All leaves</text>
            <text x="240" y="95" textAnchor="middle" fill="currentColor" fontSize="9">exist</text>
            <rect x="20" y="120" width="260" height="60" rx="4" fill="rgba(249, 115, 22, 0.1)"/>
            <text x="150" y="145" textAnchor="middle" fill="currentColor" fontSize="12" >Miden: Optimized for Notes</text>
            <text x="150" y="165" textAnchor="middle" fill="currentColor" fontSize="10">Efficient proofs + Privacy</text>
          </svg>
        )
      },
      {
        id: 'step-9',
        title: 'Building Merkle Proofs in Practice',
        content: `Let's see how to actually build a Merkle proof.

**Scenario:** You have data item "Bob" and want to prove it's in the tree.

**Step 1:** Identify your data's position
- "Bob" is at position 1 (0-indexed: positions 0,1,2,3)

**Step 2:** Collect sibling hashes along the path
- Sibling of "Bob": hash of "Alice" (position 0)
- Sibling of Parent1: Parent2
- Continue to root

**Step 3:** Provide the proof
- The data: "Bob"
- Sibling hashes: [hash_A, Parent2]
- Root hash: (known publicly)

**Step 4:** Verifier reconstructs
- Hash "Bob" → hash_B
- Combine hash_A + hash_B → Parent1
- Combine Parent1 + Parent2 → Root
- Compare with known root

**If match → proof is valid!**

**Key Point:** The proof size grows logarithmically with tree size, making it efficient even for huge datasets.`,
        visual: (
          <svg width="300" height="220" viewBox="0 0 300 220" fill="none" stroke="currentColor" strokeWidth="2">
            <text x="150" y="25" textAnchor="middle" fill="currentColor" fontSize="13" >Proof Construction Flow</text>
            <rect x="100" y="40" width="100" height="30" rx="4" fill="rgba(16, 185, 129, 0.2)"/>
            <text x="150" y="60" textAnchor="middle" fill="currentColor" fontSize="11">Step 1: Find "Bob"</text>
            <path d="M150 70 L150 90" strokeWidth="2"/>
            <rect x="100" y="90" width="100" height="30" rx="4" fill="rgba(249, 115, 22, 0.2)"/>
            <text x="150" y="110" textAnchor="middle" fill="currentColor" fontSize="11">Step 2: Get siblings</text>
            <path d="M150 120 L150 140" strokeWidth="2"/>
            <rect x="100" y="140" width="100" height="30" rx="4" fill="rgba(249, 115, 22, 0.2)"/>
            <text x="150" y="160" textAnchor="middle" fill="currentColor" fontSize="11">Step 3: Build path</text>
            <path d="M150 170 L150 190" strokeWidth="2"/>
            <circle cx="150" cy="200" r="15" fill="rgba(16, 185, 129, 0.3)"/>
            <text x="150" y="205" textAnchor="middle" fill="currentColor" fontSize="10" >✓ Valid</text>
          </svg>
        )
      },
      {
        id: 'step-10',
        title: 'Merkle Trees in Miden: Real Applications',
        content: `Now you understand Merkle trees! Let's see how Miden uses them.

**Miden's Merkle Tree Applications:**

1. **Note Management**
   - All notes (private assets) are organized in a Merkle tree
   - You can prove note ownership with a Merkle proof
   - Enables private transfers without revealing all notes

2. **Account State**
   - Account states stored in Merkle trees
   - Efficient state verification
   - Privacy-preserving balance checks

3. **Transaction Verification**
   - Transactions reference Merkle roots
   - Light clients verify without full state
   - Enables efficient mobile wallets

4. **Zero-Knowledge Integration**
   - Merkle proofs embedded in ZK proofs
   - Prove membership/ownership privately
   - Foundation of Miden's privacy model

**Why This Matters:** Merkle trees are the bridge between privacy and verifiability. They let Miden prove things are correct without revealing everything.

**Next Steps:** Now that you understand Merkle trees, you're ready to learn about STARK proofs and how they use Merkle trees for efficient zero-knowledge verification!`,
        visual: (
          <svg width="300" height="200" viewBox="0 0 300 200" fill="none" stroke="currentColor" strokeWidth="2">
            <text x="150" y="30" textAnchor="middle" fill="currentColor" fontSize="14" >Miden's Merkle Applications</text>
            <rect x="20" y="50" width="120" height="40" rx="4" fill="rgba(16, 185, 129, 0.15)"/>
            <text x="80" y="70" textAnchor="middle" fill="currentColor" fontSize="11" >Note Management</text>
            <text x="80" y="85" textAnchor="middle" fill="currentColor" fontSize="9">Private assets</text>
            <rect x="160" y="50" width="120" height="40" rx="4" fill="rgba(16, 185, 129, 0.15)"/>
            <text x="220" y="70" textAnchor="middle" fill="currentColor" fontSize="11" >Account State</text>
            <text x="220" y="85" textAnchor="middle" fill="currentColor" fontSize="9">Balance checks</text>
            <rect x="20" y="110" width="120" height="40" rx="4" fill="rgba(16, 185, 129, 0.15)"/>
            <text x="80" y="130" textAnchor="middle" fill="currentColor" fontSize="11" >Tx Verification</text>
            <text x="80" y="145" textAnchor="middle" fill="currentColor" fontSize="9">Light clients</text>
            <rect x="160" y="110" width="120" height="40" rx="4" fill="rgba(16, 185, 129, 0.15)"/>
            <text x="220" y="130" textAnchor="middle" fill="currentColor" fontSize="11" >ZK Integration</text>
            <text x="220" y="145" textAnchor="middle" fill="currentColor" fontSize="9">Private proofs</text>
            <text x="150" y="175" textAnchor="middle" fill="currentColor" fontSize="11" >Privacy + Verifiability</text>
          </svg>
        )
      }
    ]
  },
  {
    id: 'stark-proofs',
    title: 'STARK Proofs Explained',
    category: 'tutorials',
    difficulty: 'Intermediate',
    description: 'Learn the basics of STARK proof systems',
    visualTutorial: true,
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M12 2L15.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    steps: [
      {
        id: 'step-1',
        title: 'What are STARKs?',
        content: `STARK stands for **Scalable Transparent ARguments of Knowledge**. It's a type of zero-knowledge proof system that's particularly powerful for blockchain applications.

**Key Features:**
- **Scalable:** Proof size and verification time grow slowly (logarithmically) with computation size
- **Transparent:** No trusted setup required - anyone can verify
- **Fast:** Verification is extremely quick, even for complex computations
- **Quantum-resistant:** Based on hash functions, not elliptic curves

**Why STARKs Matter:** They enable private, verifiable computation at scale. Miden uses STARKs to prove transactions are valid without revealing details.`,
        visual: (
          <svg width="300" height="180" viewBox="0 0 300 180" fill="none" stroke="currentColor" strokeWidth="2">
            <text x="150" y="25" textAnchor="middle" fill="currentColor" fontSize="14" >STARK Proof System</text>
            <rect x="20" y="50" width="120" height="100" rx="6" fill="rgba(16, 185, 129, 0.15)"/>
            <text x="80" y="75" textAnchor="middle" fill="currentColor" fontSize="12" >Computation</text>
            <text x="80" y="95" textAnchor="middle" fill="currentColor" fontSize="10">Private inputs</text>
            <text x="80" y="115" textAnchor="middle" fill="currentColor" fontSize="10">Complex logic</text>
            <text x="80" y="135" textAnchor="middle" fill="currentColor" fontSize="10">Many steps</text>
            <path d="M140 100 L160 100" strokeWidth="3" strokeDasharray="8,4"/>
            <rect x="160" y="50" width="120" height="100" rx="6" fill="rgba(249, 115, 22, 0.15)"/>
            <text x="220" y="75" textAnchor="middle" fill="currentColor" fontSize="12" >STARK Proof</text>
            <text x="220" y="95" textAnchor="middle" fill="currentColor" fontSize="10">Small size</text>
            <text x="220" y="115" textAnchor="middle" fill="currentColor" fontSize="10">Fast verify</text>
            <text x="220" y="135" textAnchor="middle" fill="currentColor" fontSize="10">No secrets</text>
          </svg>
        )
      },
      {
        id: 'step-2',
        title: 'How STARKs Work',
        content: `STARKs convert computations into mathematical proofs through several steps:

**Step 1: Arithmetic Circuit**
- Your computation becomes a series of mathematical operations
- Each operation is a constraint that must be satisfied

**Step 2: Polynomial Encoding**
- The computation is encoded as polynomials
- Polynomials can represent complex relationships efficiently

**Step 3: Low-Degree Testing**
- The system checks if polynomials have the right structure
- Uses advanced math (Reed-Solomon codes) to verify

**Step 4: Proof Generation**
- A compact proof is generated
- Contains enough information to verify without revealing inputs

**The Result:** A tiny proof that proves your computation is correct, without showing what you computed!`,
        visual: (
          <svg width="300" height="240" viewBox="0 0 300 240" fill="none" stroke="currentColor" strokeWidth="2">
            <text x="150" y="25" textAnchor="middle" fill="currentColor" fontSize="13" >STARK Proof Generation</text>
            <rect x="80" y="40" width="140" height="30" rx="4" fill="rgba(16, 185, 129, 0.2)"/>
            <text x="150" y="60" textAnchor="middle" fill="currentColor" fontSize="11">1. Arithmetic Circuit</text>
            <path d="M150 70 L150 90" strokeWidth="2"/>
            <rect x="80" y="90" width="140" height="30" rx="4" fill="rgba(16, 185, 129, 0.2)"/>
            <text x="150" y="110" textAnchor="middle" fill="currentColor" fontSize="11">2. Polynomial Encoding</text>
            <path d="M150 120 L150 140" strokeWidth="2"/>
            <rect x="80" y="140" width="140" height="30" rx="4" fill="rgba(16, 185, 129, 0.2)"/>
            <text x="150" y="160" textAnchor="middle" fill="currentColor" fontSize="11">3. Low-Degree Testing</text>
            <path d="M150 170 L150 190" strokeWidth="2"/>
            <circle cx="150" cy="205" r="20" fill="rgba(249, 115, 22, 0.3)"/>
            <text x="150" y="210" textAnchor="middle" fill="currentColor" fontSize="10" >Proof</text>
          </svg>
        )
      },
      {
        id: 'step-3',
        title: 'STARKs vs Other Proof Systems',
        content: `STARKs have unique advantages over other zero-knowledge proof systems:

**vs SNARKs:**
- STARKs: No trusted setup (transparent)
- SNARKs: Require trusted setup ceremony
- STARKs: Quantum-resistant
- SNARKs: May be vulnerable to quantum computers

**vs Bulletproofs:**
- STARKs: Better for large computations
- Bulletproofs: Better for small proofs
- STARKs: Faster verification
- Bulletproofs: Smaller proof size (for small cases)

**Why Miden Chose STARKs:**
- Transparency: No trust required
- Scalability: Handle complex transactions
- Security: Future-proof against quantum threats
- Performance: Fast verification for users`,
        visual: (
          <svg width="300" height="200" viewBox="0 0 300 200" fill="none" stroke="currentColor" strokeWidth="2">
            <text x="150" y="25" textAnchor="middle" fill="currentColor" fontSize="13" >Proof System Comparison</text>
            <rect x="20" y="50" width="80" height="120" rx="4" fill="rgba(100, 116, 139, 0.2)"/>
            <text x="60" y="75" textAnchor="middle" fill="currentColor" fontSize="11" >SNARKs</text>
            <text x="60" y="95" textAnchor="middle" fill="currentColor" fontSize="9">✗ Trusted setup</text>
            <text x="60" y="115" textAnchor="middle" fill="currentColor" fontSize="9">✗ Quantum risk</text>
            <text x="60" y="135" textAnchor="middle" fill="currentColor" fontSize="9">✓ Small proofs</text>
            <rect x="110" y="50" width="80" height="120" rx="4" fill="rgba(249, 115, 22, 0.2)"/>
            <text x="150" y="75" textAnchor="middle" fill="currentColor" fontSize="11" >STARKs</text>
            <text x="150" y="95" textAnchor="middle" fill="currentColor" fontSize="9">✓ No setup</text>
            <text x="150" y="115" textAnchor="middle" fill="currentColor" fontSize="9">✓ Quantum-safe</text>
            <text x="150" y="135" textAnchor="middle" fill="currentColor" fontSize="9">✓ Scalable</text>
            <text x="150" y="155" textAnchor="middle" fill="currentColor" fontSize="9" >Miden's Choice</text>
            <rect x="200" y="50" width="80" height="120" rx="4" fill="rgba(100, 116, 139, 0.2)"/>
            <text x="240" y="75" textAnchor="middle" fill="currentColor" fontSize="11" >Bulletproofs</text>
            <text x="240" y="95" textAnchor="middle" fill="currentColor" fontSize="9">✓ No setup</text>
            <text x="240" y="115" textAnchor="middle" fill="currentColor" fontSize="9">✗ Small scale</text>
            <text x="240" y="135" textAnchor="middle" fill="currentColor" fontSize="9">✗ Slower verify</text>
          </svg>
        )
      },
      {
        id: 'step-4',
        title: 'STARKs in Miden',
        content: `Miden uses STARKs to enable private, verifiable transactions on the blockchain.

**How Miden Uses STARKs:**

1. **Transaction Execution**
   - Your transaction runs on your device (private)
   - Miden VM executes the logic
   - All computation stays local

2. **Proof Generation**
   - Your device generates a STARK proof
   - Proof shows transaction is valid
   - Doesn't reveal transaction details

3. **Blockchain Submission**
   - Only the proof is sent to blockchain
   - Blockchain verifies proof quickly
   - Transaction is confirmed

4. **Privacy Preserved**
   - Amounts stay hidden
   - Account details stay hidden
   - Only validity is proven

**The Magic:** STARKs let Miden prove transactions are correct without revealing what the transaction does!`,
        visual: (
          <svg width="300" height="240" viewBox="0 0 300 240" fill="none" stroke="currentColor" strokeWidth="2">
            <text x="150" y="25" textAnchor="middle" fill="currentColor" fontSize="13" >Miden's STARK Flow</text>
            <rect x="20" y="50" width="120" height="50" rx="6" fill="rgba(16, 185, 129, 0.15)"/>
            <text x="80" y="75" textAnchor="middle" fill="currentColor" fontSize="11" >Your Device</text>
            <text x="80" y="95" textAnchor="middle" fill="currentColor" fontSize="9">Execute transaction</text>
            <path d="M140 75 L160 75" strokeWidth="3" strokeDasharray="8,4"/>
            <rect x="160" y="50" width="120" height="50" rx="6" fill="rgba(249, 115, 22, 0.15)"/>
            <text x="220" y="75" textAnchor="middle" fill="currentColor" fontSize="11" >Generate Proof</text>
            <text x="220" y="95" textAnchor="middle" fill="currentColor" fontSize="9">STARK proof</text>
            <path d="M220 100 L220 120" strokeWidth="2"/>
            <rect x="80" y="120" width="140" height="40" rx="6" fill="rgba(15, 23, 42, 0.6)"/>
            <text x="150" y="145" textAnchor="middle" fill="currentColor" fontSize="11" >Send to Blockchain</text>
            <text x="150" y="160" textAnchor="middle" fill="currentColor" fontSize="9">Only proof, no details</text>
            <path d="M150 160 L150 180" strokeWidth="2"/>
            <rect x="80" y="180" width="140" height="40" rx="6" fill="rgba(16, 185, 129, 0.2)"/>
            <text x="150" y="200" textAnchor="middle" fill="currentColor" fontSize="11" >✓ Verified & Confirmed</text>
            <text x="150" y="215" textAnchor="middle" fill="currentColor" fontSize="9">Privacy preserved!</text>
      </svg>
        )
      },
      {
        id: 'step-5',
        title: 'STARK Performance',
        content: `STARKs are designed for performance at scale.

**Proof Generation:**
- Time: Grows logarithmically with computation size
- For a transaction: Seconds to minutes
- Happens on your device (no server needed)

**Proof Size:**
- Small proofs: ~100-200 KB
- Grows slowly with complexity
- Much smaller than full computation

**Verification:**
- Extremely fast: Milliseconds
- Can verify on any device
- No special hardware needed

**Real-World Impact:**
- Mobile wallets can verify proofs
- Light clients work efficiently
- Scalable to millions of transactions

**Miden's Advantage:** STARKs make private transactions practical for everyday use, not just for experts!`,
        visual: (
          <svg width="300" height="200" viewBox="0 0 300 200" fill="none" stroke="currentColor" strokeWidth="2">
            <text x="150" y="25" textAnchor="middle" fill="currentColor" fontSize="13" >STARK Performance</text>
            <rect x="20" y="50" width="120" height="130" rx="6" fill="rgba(15, 23, 42, 0.6)"/>
            <text x="80" y="75" textAnchor="middle" fill="currentColor" fontSize="12" >Computation</text>
            <text x="80" y="100" textAnchor="middle" fill="currentColor" fontSize="10">1,000,000</text>
            <text x="80" y="115" textAnchor="middle" fill="currentColor" fontSize="10">operations</text>
            <text x="80" y="140" textAnchor="middle" fill="currentColor" fontSize="10">Complex logic</text>
            <text x="80" y="155" textAnchor="middle" fill="currentColor" fontSize="10">Many steps</text>
            <path d="M140 115 L160 115" strokeWidth="3" strokeDasharray="8,4"/>
            <text x="150" y="110" textAnchor="middle" fill="currentColor" fontSize="10" >STARK</text>
            <rect x="160" y="50" width="120" height="130" rx="6" fill="rgba(16, 185, 129, 0.2)"/>
            <text x="220" y="75" textAnchor="middle" fill="currentColor" fontSize="12" >Proof</text>
            <text x="220" y="100" textAnchor="middle" fill="currentColor" fontSize="10">~200 KB</text>
            <text x="220" y="120" textAnchor="middle" fill="currentColor" fontSize="10">Verify: ~10ms</text>
            <text x="220" y="145" textAnchor="middle" fill="currentColor" fontSize="10" >Efficient!</text>
            <text x="220" y="165" textAnchor="middle" fill="currentColor" fontSize="9" fillOpacity="0.6">Logarithmic growth</text>
          </svg>
        )
      }
    ]
  },
  {
    id: 'advanced-stack',
    title: 'Advanced Stack Operations',
    category: 'games',
    difficulty: 'Intermediate',
    description: 'Master complex stack manipulations for sophisticated zero-knowledge programs',
    visualTutorial: true,
    steps: [
      {
        id: 'step-1',
        title: 'Stack Depth Management - Optimizing ZK Circuits',
        content: `Managing stack depth efficiently is crucial for optimized zero-knowledge proofs. Smaller circuits mean faster proof generation.

\`\`\`masm
push 1
push 2
push 3
push 4
pop
pop
\`\`\`

This leaves only 1 and 2 on the stack.

**Performance Tip:** In ZK systems, every operation adds to the circuit size. Efficient stack management reduces proof generation time and keeps your zkApps fast and practical. Modern ZK optimizations focus heavily on minimizing circuit complexity!`,
        visual: (
          <svg width="300" height="220" viewBox="0 0 300 220" fill="none" stroke="currentColor" strokeWidth="2">
            <text x="150" y="25" textAnchor="middle" fill="currentColor" fontSize="13" >Stack Depth Management</text>
            <rect x="100" y="50" width="100" height="140" rx="6" fill="rgba(15, 23, 42, 0.6)" strokeWidth="3"/>
            <text x="150" y="75" textAnchor="middle" fill="currentColor" fontSize="11" >Stack</text>
            <rect x="110" y="90" width="80" height="20" rx="3" fill="rgba(16, 185, 129, 0.3)"/>
            <text x="150" y="105" textAnchor="middle" fill="currentColor" fontSize="10">4 (top)</text>
            <rect x="110" y="115" width="80" height="20" rx="3" fill="rgba(16, 185, 129, 0.3)"/>
            <text x="150" y="130" textAnchor="middle" fill="currentColor" fontSize="10">3</text>
            <rect x="110" y="140" width="80" height="20" rx="3" fill="rgba(249, 115, 22, 0.3)"/>
            <text x="150" y="155" textAnchor="middle" fill="currentColor" fontSize="10">2</text>
            <rect x="110" y="165" width="80" height="20" rx="3" fill="rgba(249, 115, 22, 0.3)"/>
            <text x="150" y="180" textAnchor="middle" fill="currentColor" fontSize="10">1 (bottom)</text>
            <text x="150" y="205" textAnchor="middle" fill="currentColor" fontSize="11" >After pop, pop: Only 1 and 2 remain</text>
          </svg>
        )
      }
    ]
  },
  // Advanced tutorials
  {
    id: 'miden-vm-deep-dive',
    title: 'Miden VM Deep Dive',
    category: 'tutorials',
    difficulty: 'Advanced',
    description: 'Comprehensive guide to the Miden virtual machine',
    visualTutorial: true,
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
        <line x1="8" y1="21" x2="16" y2="21" stroke="currentColor" strokeWidth="2"/>
        <line x1="12" y1="17" x2="12" y2="21" stroke="currentColor" strokeWidth="2"/>
      </svg>
    ),
    steps: [
      {
        id: 'step-1',
        title: 'Miden VM Architecture',
        content: `The Miden Virtual Machine is a stack-based VM designed specifically for zero-knowledge proofs.

**Core Components:**

1. **Stack**
   - 16-element stack for operations
   - Last-in-first-out (LIFO) structure
   - Primary workspace for computations

2. **Memory**
   - Random-access memory for storage
   - Used for complex state management
   - Enables sophisticated programs

3. **Execution Engine**
   - Processes Miden Assembly instructions
   - Maintains execution trace
   - Generates proof data

**Key Design:** Everything is optimized for generating efficient zero-knowledge proofs while maintaining privacy.`,
        visual: (
          <svg width="300" height="200" viewBox="0 0 300 200" fill="none" stroke="currentColor" strokeWidth="2">
            <text x="150" y="25" textAnchor="middle" fill="currentColor" fontSize="13" >Miden VM Components</text>
            <rect x="20" y="50" width="80" height="130" rx="6" fill="rgba(16, 185, 129, 0.15)"/>
            <text x="60" y="75" textAnchor="middle" fill="currentColor" fontSize="11" >Stack</text>
            <text x="60" y="95" textAnchor="middle" fill="currentColor" fontSize="9">16 elements</text>
            <text x="60" y="115" textAnchor="middle" fill="currentColor" fontSize="9">LIFO</text>
            <text x="60" y="135" textAnchor="middle" fill="currentColor" fontSize="9">Operations</text>
            <rect x="110" y="50" width="80" height="130" rx="6" fill="rgba(249, 115, 22, 0.15)"/>
            <text x="150" y="75" textAnchor="middle" fill="currentColor" fontSize="11" >Memory</text>
            <text x="150" y="95" textAnchor="middle" fill="currentColor" fontSize="9">Random access</text>
            <text x="150" y="115" textAnchor="middle" fill="currentColor" fontSize="9">State storage</text>
            <text x="150" y="135" textAnchor="middle" fill="currentColor" fontSize="9">Complex data</text>
            <rect x="200" y="50" width="80" height="130" rx="6" fill="rgba(100, 116, 139, 0.15)"/>
            <text x="240" y="75" textAnchor="middle" fill="currentColor" fontSize="11" >Engine</text>
            <text x="240" y="95" textAnchor="middle" fill="currentColor" fontSize="9">Executes code</text>
            <text x="240" y="115" textAnchor="middle" fill="currentColor" fontSize="9">Generates proof</text>
            <text x="240" y="135" textAnchor="middle" fill="currentColor" fontSize="9">Maintains trace</text>
          </svg>
        )
      },
      {
        id: 'step-2',
        title: 'Stack-Based Execution',
        content: `Miden VM uses a stack-based execution model, similar to Forth or PostScript.

**How It Works:**
- Values are pushed onto the stack
- Operations consume values from the stack
- Results are pushed back onto the stack

**Example Flow:**
1. Push 5 → Stack: [5]
2. Push 3 → Stack: [5, 3]
3. Add → Pops 5 and 3, pushes 8 → Stack: [8]

**Advantages:**
- Simple instruction set
- Efficient for ZK proofs
- Easy to reason about
- Compact representation

**Why Stack-Based:** Stack operations are easy to prove in zero-knowledge. Each operation is a simple constraint that can be verified efficiently.`,
        visual: (
          <svg width="300" height="240" viewBox="0 0 300 240" fill="none" stroke="currentColor" strokeWidth="2">
            <text x="150" y="25" textAnchor="middle" fill="currentColor" fontSize="13" >Stack Execution Flow</text>
            <rect x="100" y="50" width="100" height="160" rx="6" fill="rgba(15, 23, 42, 0.6)"/>
            <text x="150" y="75" textAnchor="middle" fill="currentColor" fontSize="11" >Stack</text>
            <rect x="110" y="90" width="80" height="20" rx="3" fill="rgba(16, 185, 129, 0.3)"/>
            <text x="150" y="105" textAnchor="middle" fill="currentColor" fontSize="10">Step 1: Push 5</text>
            <rect x="110" y="120" width="80" height="20" rx="3" fill="rgba(16, 185, 129, 0.3)"/>
            <text x="150" y="135" textAnchor="middle" fill="currentColor" fontSize="10">Step 2: Push 3</text>
            <rect x="110" y="150" width="80" height="20" rx="3" fill="rgba(249, 115, 22, 0.3)"/>
            <text x="150" y="165" textAnchor="middle" fill="currentColor" fontSize="10">Step 3: Add</text>
            <rect x="110" y="180" width="80" height="20" rx="3" fill="rgba(16, 185, 129, 0.4)"/>
            <text x="150" y="195" textAnchor="middle" fill="currentColor" fontSize="10" >Result: 8</text>
            <text x="150" y="220" textAnchor="middle" fill="currentColor" fontSize="10">Stack: [8]</text>
          </svg>
        )
      },
      {
        id: 'step-3',
        title: 'Memory Operations',
        content: `Miden VM supports memory for storing and retrieving values during execution.

**Memory Operations:**

1. **Allocate Memory**
   - Reserve space for your program
   - Specify how many words you need
   - Memory is private by default

2. **Store Values**
   - Save values to specific addresses
   - Values stay in memory
   - Can be retrieved later

3. **Load Values**
   - Retrieve values from addresses
   - Values are copied to stack
   - Original stays in memory

**Use Cases:**
- Storing intermediate results
- Managing complex state
- Building data structures
- Enabling sophisticated programs

**Privacy:** Memory contents are private. The proof shows operations are valid without revealing stored values!`,
        visual: (
          <svg width="300" height="220" viewBox="0 0 300 220" fill="none" stroke="currentColor" strokeWidth="2">
            <text x="150" y="25" textAnchor="middle" fill="currentColor" fontSize="13" >Memory Operations</text>
            <rect x="20" y="50" width="120" height="150" rx="6" fill="rgba(15, 23, 42, 0.6)"/>
            <text x="80" y="75" textAnchor="middle" fill="currentColor" fontSize="11" >Memory</text>
            <rect x="30" y="90" width="100" height="20" rx="3" fill="rgba(16, 185, 129, 0.3)"/>
            <text x="80" y="105" textAnchor="middle" fill="currentColor" fontSize="10">Address 0: Value</text>
            <rect x="30" y="120" width="100" height="20" rx="3" fill="rgba(16, 185, 129, 0.3)"/>
            <text x="80" y="135" textAnchor="middle" fill="currentColor" fontSize="10">Address 1: Value</text>
            <rect x="30" y="150" width="100" height="20" rx="3" fill="rgba(100, 116, 139, 0.2)"/>
            <text x="80" y="165" textAnchor="middle" fill="currentColor" fontSize="10">Address 2: Empty</text>
            <path d="M140 115 L180 115" strokeWidth="3" strokeDasharray="8,4"/>
            <rect x="180" y="50" width="100" height="150" rx="6" fill="rgba(249, 115, 22, 0.15)"/>
            <text x="230" y="75" textAnchor="middle" fill="currentColor" fontSize="11" >Stack</text>
            <rect x="190" y="100" width="80" height="20" rx="3" fill="rgba(16, 185, 129, 0.3)"/>
            <text x="230" y="115" textAnchor="middle" fill="currentColor" fontSize="10">Load from 0</text>
            <text x="230" y="140" textAnchor="middle" fill="currentColor" fontSize="9">Values copied</text>
            <text x="230" y="160" textAnchor="middle" fill="currentColor" fontSize="9">to stack</text>
            <text x="230" y="180" textAnchor="middle" fill="currentColor" fontSize="9" fillOpacity="0.6">Memory stays private</text>
          </svg>
        )
      },
      {
        id: 'step-4',
        title: 'Execution Model',
        content: `Miden VM executes programs step by step, maintaining a complete execution trace.

**Execution Process:**

1. **Parse Instructions**
   - Read Miden Assembly code
   - Convert to internal representation
   - Validate syntax

2. **Execute Step by Step**
   - Process each instruction
   - Update stack and memory
   - Track all state changes

3. **Build Execution Trace**
   - Record every operation
   - Capture all state transitions
   - Prepare for proof generation

4. **Generate Proof**
   - Convert trace to proof
   - Create STARK proof
   - Verify correctness

**Key Feature:** The execution trace is what gets proven. It shows the computation is correct without revealing inputs or intermediate values.`,
        visual: (
          <svg width="300" height="240" viewBox="0 0 300 240" fill="none" stroke="currentColor" strokeWidth="2">
            <text x="150" y="25" textAnchor="middle" fill="currentColor" fontSize="13" >Execution Model</text>
            <rect x="80" y="40" width="140" height="30" rx="4" fill="rgba(16, 185, 129, 0.2)"/>
            <text x="150" y="60" textAnchor="middle" fill="currentColor" fontSize="11">1. Parse Instructions</text>
            <path d="M150 70 L150 90" strokeWidth="2"/>
            <rect x="80" y="90" width="140" height="30" rx="4" fill="rgba(16, 185, 129, 0.2)"/>
            <text x="150" y="110" textAnchor="middle" fill="currentColor" fontSize="11">2. Execute Step by Step</text>
            <path d="M150 120 L150 140" strokeWidth="2"/>
            <rect x="80" y="140" width="140" height="30" rx="4" fill="rgba(16, 185, 129, 0.2)"/>
            <text x="150" y="160" textAnchor="middle" fill="currentColor" fontSize="11">3. Build Execution Trace</text>
            <path d="M150 170 L150 190" strokeWidth="2"/>
            <circle cx="150" cy="205" r="20" fill="rgba(249, 115, 22, 0.3)"/>
            <text x="150" y="210" textAnchor="middle" fill="currentColor" fontSize="10" >Proof</text>
          </svg>
        )
      },
      {
        id: 'step-5',
        title: 'Zero-Knowledge Integration',
        content: `Miden VM is designed from the ground up for zero-knowledge proofs.

**ZK-Optimized Design:**

1. **Deterministic Execution**
   - Same inputs always produce same outputs
   - Essential for proof generation
   - Enables verification

2. **Trace Structure**
   - Every operation is recorded
   - State transitions are tracked
   - Enables efficient proofs

3. **Privacy by Default**
   - Inputs are private
   - Intermediate values are private
   - Only outputs can be revealed

4. **Proof Generation**
   - Execution trace becomes proof
   - STARKs prove correctness
   - No secrets revealed

**The Result:** You can prove any computation is correct without revealing what you computed or what inputs you used. This is the power of zero-knowledge virtual machines!`,
        visual: (
          <svg width="300" height="200" viewBox="0 0 300 200" fill="none" stroke="currentColor" strokeWidth="2">
            <text x="150" y="25" textAnchor="middle" fill="currentColor" fontSize="13" >ZK-Optimized VM</text>
            <rect x="20" y="50" width="120" height="130" rx="6" fill="rgba(16, 185, 129, 0.15)"/>
            <text x="80" y="75" textAnchor="middle" fill="currentColor" fontSize="11" >Private</text>
            <text x="80" y="100" textAnchor="middle" fill="currentColor" fontSize="10">Inputs</text>
            <text x="80" y="120" textAnchor="middle" fill="currentColor" fontSize="10">State</text>
            <text x="80" y="140" textAnchor="middle" fill="currentColor" fontSize="10">Operations</text>
            <text x="80" y="160" textAnchor="middle" fill="currentColor" fontSize="10">Trace</text>
            <path d="M140 115 L160 115" strokeWidth="3" strokeDasharray="8,4"/>
            <rect x="160" y="50" width="120" height="130" rx="6" fill="rgba(249, 115, 22, 0.15)"/>
            <text x="220" y="75" textAnchor="middle" fill="currentColor" fontSize="11" >Public Proof</text>
            <text x="220" y="100" textAnchor="middle" fill="currentColor" fontSize="10">✓ Valid</text>
            <text x="220" y="120" textAnchor="middle" fill="currentColor" fontSize="10">✓ Correct</text>
            <text x="220" y="140" textAnchor="middle" fill="currentColor" fontSize="9" fillOpacity="0.6">✗ No details</text>
            <text x="220" y="160" textAnchor="middle" fill="currentColor" fontSize="9" fillOpacity="0.6">✗ No inputs</text>
          </svg>
        )
      }
    ]
  },
  {
    id: 'memory-management',
    title: 'Memory Management for Private Applications',
    category: 'experiments',
    difficulty: 'Advanced',
    description: 'Learn to manage private state in zero-knowledge applications',
    visualTutorial: true,
    steps: [
      {
        id: 'step-1',
        title: 'Private State Management in ZK Applications',
        content: `Proper memory management enables complex privacy-preserving applications. Store multiple private values and compute over them without revealing any individual value!

\`\`\`masm
alloc 5
push 100
push 0
store
push 200
push 1
store
push 0
load
push 1
load
add
\`\`\`

This stores two values in memory and adds them.

**Real-World ZK Use Case:** This pattern powers privacy-preserving DeFi! Imagine:
- Address 0: Your private balance
- Address 1: Transaction amount
- The sum: Proving sufficient funds without revealing your exact balance

This is how zkRollups, private DEXs, and confidential payment systems maintain privacy while ensuring correctness!`,
        visual: (
          <svg width="300" height="240" viewBox="0 0 300 240" fill="none" stroke="currentColor" strokeWidth="2">
            <text x="150" y="25" textAnchor="middle" fill="currentColor" fontSize="13" >Private State Management</text>
            <rect x="20" y="50" width="120" height="160" rx="6" fill="rgba(15, 23, 42, 0.6)"/>
            <text x="80" y="75" textAnchor="middle" fill="currentColor" fontSize="11" >Memory</text>
            <rect x="30" y="90" width="100" height="25" rx="3" fill="rgba(16, 185, 129, 0.2)"/>
            <text x="80" y="108" textAnchor="middle" fill="currentColor" fontSize="10">Address 0</text>
            <text x="80" y="120" textAnchor="middle" fill="currentColor" fontSize="9" fillOpacity="0.6">Balance (hidden)</text>
            <rect x="30" y="130" width="100" height="25" rx="3" fill="rgba(16, 185, 129, 0.2)"/>
            <text x="80" y="148" textAnchor="middle" fill="currentColor" fontSize="10">Address 1</text>
            <text x="80" y="160" textAnchor="middle" fill="currentColor" fontSize="9" fillOpacity="0.6">Amount (hidden)</text>
            <rect x="30" y="170" width="100" height="25" rx="3" fill="rgba(249, 115, 22, 0.2)"/>
            <text x="80" y="188" textAnchor="middle" fill="currentColor" fontSize="10">Result</text>
            <text x="80" y="200" textAnchor="middle" fill="currentColor" fontSize="9" fillOpacity="0.6">Sum (hidden)</text>
            <path d="M140 145 L180 145" strokeWidth="3" strokeDasharray="8,4"/>
            <rect x="180" y="50" width="100" height="160" rx="6" fill="rgba(249, 115, 22, 0.1)"/>
            <text x="230" y="75" textAnchor="middle" fill="currentColor" fontSize="11" >Proof Shows</text>
            <text x="230" y="100" textAnchor="middle" fill="currentColor" fontSize="10">✓ Load valid</text>
            <text x="230" y="120" textAnchor="middle" fill="currentColor" fontSize="10">✓ Add valid</text>
            <text x="230" y="140" textAnchor="middle" fill="currentColor" fontSize="10">✓ Result correct</text>
            <text x="230" y="170" textAnchor="middle" fill="currentColor" fontSize="9" fillOpacity="0.6">✗ Values hidden</text>
            <text x="230" y="190" textAnchor="middle" fill="currentColor" fontSize="9" fillOpacity="0.6">✗ Addresses hidden</text>
          </svg>
        )
      }
    ]
  }
];

